import { Button, Collapse, Stack, Typography } from '@mui/material';
import { alpha, type SxProps, type Theme } from '@mui/material/styles';
import { memo, useState } from 'react';
import {
  callTranscriptBoxSx,
  callTranscriptHeaderSx,
} from 'src/pages/Conversations/Chat/ChatMessage/call-transcript.styles';
import { useDisplayTime } from 'src/utils/hooks/use-display-time';
import type { SmsSessionGroup } from './sms-session-grouping';
import { SmsSessionThread } from './sms-session-thread';

const sessionCardBoxSx: SxProps<Theme> = [callTranscriptBoxSx, { boxShadow: 'none' }].flat();

const endedChipSx: SxProps<Theme> = {
  px: 0.75,
  py: 0.125,
  ml: 0.5,
  borderRadius: 100,
  fontSize: '0.625rem',
  fontWeight: 700,
  letterSpacing: '0.04em',
  lineHeight: 1.6,
  flexShrink: 0,
  color: 'text.secondary',
  backgroundColor: (theme) => alpha(theme.palette.text.primary, 0.08),
};

const transcriptToggleSx = (isExpanded: boolean): SxProps<Theme> => ({
  ml: 'auto',
  px: 1,
  py: 0.25,
  minWidth: 0,
  flexShrink: 0,
  borderRadius: 100,
  fontSize: '0.6875rem',
  fontWeight: 700,
  letterSpacing: '0.04em',
  color: 'primary.main',
  backgroundColor: (theme) =>
    isExpanded ? alpha(theme.palette.primary.main, 0.12) : 'transparent',
  '&:hover': {
    backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.12),
  },
});

export const SmsSessionTimelineCard = memo(function SmsSessionTimelineCard({
  group,
}: {
  group: SmsSessionGroup;
}) {
  const [isExpanded, setIsExpanded] = useState(false);
  const { displayTime } = useDisplayTime();

  const endLabel = displayTime(group.endedAt);
  const count = group.messageCount;

  return (
    <Stack sx={sessionCardBoxSx}>
      <Stack direction="row" sx={callTranscriptHeaderSx(isExpanded)}>
        <Typography variant="body2" fontWeight={600} sx={{ flexShrink: 0 }}>
          SMS session
        </Typography>
        <Typography variant="body2" color="text.secondary" noWrap sx={{ minWidth: 0 }}>
          · {count} message{count !== 1 ? 's' : ''} · ended · {endLabel}
        </Typography>
        <Typography component="span" sx={endedChipSx}>
          ENDED
        </Typography>
        <Button
          size="small"
          onClick={() => setIsExpanded((v) => !v)}
          sx={transcriptToggleSx(isExpanded)}
        >
          TRANSCRIPT
        </Button>
      </Stack>

      <Collapse in={isExpanded}>
        <SmsSessionThread group={group} />
      </Collapse>
    </Stack>
  );
});
